import axios from 'axios'
import { PatternlibLanguageChangeItem } from "@liebherr/patternlib-vue";
import { useAuth } from "../stores/authStore";
import { useGlobal } from "../stores/globalStore";

export default class APIService {
  constructor(callbackLogout) {
    this.authStore = useAuth();
    this.callbackLogout = callbackLogout;
    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        if (error.response.status == 401) {
          this.callbackLogout();
        }
        return error;
      }
    );
  }

  _getConfig() {
    return {
      baseURL: import.meta.env.VITE_APP_BACKEND_API_URL
    };
  }

  _getAuthConfig() {
    //console.log(`User loggedin: ${this.authStore.isLoggedIn}`)
    if (!this.authStore.isLoggedIn) {
      throw new Error(
        "User is not logged in! No Authorized API calls could be made"
      );
    }
    return {
      baseURL: import.meta.env.VITE_APP_BACKEND_API_URL,
      headers: {
        Authorization: `Bearer ${this.authStore.access_token}`
      }
    };
  }

  getDocuments(callback, documents, language, country) {
    let res = axios
      .get("Legal/GetDocuments", {
        params: {
          documents: documents,
          language: language,
          country: country
        },
        ...this._getConfig()
      })
      .then((res) => callback(res.data.data));
  }

  getIsShopCountry(callback, alpha2) {
    axios
      .get(`Country/${alpha2}/shop`, this._getAuthConfig())
      .then((res) => {
        callback(res.status === 200);
        //set check prop (get request will repeated on each page change while there was no success yet)
        const globalStore = useGlobal();
        globalStore.hasCheckedShopCountry = true;
      })
      .catch((err) => {
        //default value for error case
        callback(true);
      });
  }

  newDocumentsToAccept(callback, language, country) {
    if (localStorage.getItem("pendingLegalDocumentsToAccept")) {
      let documentIds = JSON.parse(
        localStorage.getItem("pendingLegalDocumentsToAccept")
      );
      localStorage.removeItem("pendingLegalDocumentsToAccept");
      this.acceptDocuments((successful) => {
        this.newDocumentsToAccept(callback, language, country);
      }, documentIds);
      return;
    }
    axios
      .get("Legal/NewDocumentsToAccept", {
        params: {
          language: language,
          country: country
        },
        ...this._getAuthConfig()
      })
      .then((res) => callback(res.data.data));
  }

  acceptDocuments(callback, documentIds) {
    if (!this.authStore.isLoggedIn) {
      localStorage.setItem(
        "pendingLegalDocumentsToAccept",
        JSON.stringify(documentIds)
      );
      return;
    }
    axios
      .post("Legal/acceptDocuments", documentIds, this._getAuthConfig())
      .then((res) => callback(res.data.data))
      .catch((error) => callback(false));
  }

  getAllOrders(callback) {
    axios
      .get("Order/GetAll", this._getAuthConfig())
      .then((res) => callback(res.data.data));
  }

  getProductPictureUrl(callback, articlenumber) {
    axios
      .get("Order/GetProductPictureUrl", {
        params: {
          articlenumber: articlenumber
        },
        ...this._getConfig()
      })
      .then((res) => callback(res.data.data));
  }

  getUserData(callback) {
    axios
      .get("Account/GetUserData", this._getAuthConfig())
      .then((res) => callback(res.data.data));
  }

  updateUserData(callback, userData) {
    axios
      .post("Account/UpdateUserData", userData, this._getAuthConfig())
      .then((res) => callback(res.data.statusCode == 200));
  }

  changePassword(callback, currentPassword, newPassword) {
    currentPassword = btoa(currentPassword);
    newPassword = btoa(newPassword);
    axios
      .post(
        "Account/ChangePassword",
        {
          currentPassword: currentPassword,
          newPassword: newPassword
        },
        this._getAuthConfig()
      )
      .then((res) => callback(res.data.data))
      .catch((error) => callback(false));
  }

  registerUser(callback, data) {
    data.password = btoa(data.password);
    axios
      .post("Account/RegistrateUser", data, this._getConfig())
      .then((res) => {
        callback(res.data);
      });
  }

  confirmMail(callback, sessionId, code) {
    axios
      .get("Account/ConfirmMail", {
        params: {
          sessionId: sessionId,
          code: code
        },
        ...this._getConfig()
      })
      .then((res) => {
        callback(res.data.data);
      });
  }

  deleteAccount(callback) {
    axios.get("Account/deleteAccount", this._getAuthConfig()).then((res) => {
      callback(res.data.data);
    });
  }

  requestPasswordReset(callback, email, language, country) {
    axios
      .get("Account/RequestPasswordReset", {
        params: {
          email: email,
          language: language,
          country: country
        },
        ...this._getConfig()
      })
      .then((res) => {
        callback(res.data.data);
      });
  }

  isValidPasswordResetConformationCode(callback, code) {
    axios
      .post(
        "Account/IsValidPasswordResetConformationCode",
        {
          ConformationCode: code
        },
        this._getConfig()
      )
      .then((res) => callback(res.data.data))
      .catch((ex) => callback(false));
  }

  resetToNewPassword(callback, code, newPassword) {
    axios
      .post(
        "Account/ResetToNewPassword",
        {
          ConformationCode: code,
          NewPassword: newPassword
        },
        this._getConfig()
      )
      .then((res) => callback(res.data.data))
      .catch((ex) => callback(false));
  }

  getAppliances(callback) {
    axios
      .get("Appliance/GetAppliances", this._getAuthConfig())
      .then((res) => callback(res.data.data))
      .catch((ex) => callback(null));
  }

  getImageUrl(serialnumber) {
    let baseURL = import.meta.env.VITE_APP_BACKEND_API_URL;
    if (baseURL.endsWith("/")) {
      baseURL = baseURL.substring(0, baseURL.length - 1);
    }
    return `${baseURL}/Appliance/Image/${serialnumber}`;
  }

  getApplianceInfo(callback, serialnumber) {
    axios
      .get("Appliance/GetApplianceInfo", {
        params: {
          serialnumber: serialnumber
        },
        ...this._getAuthConfig()
      })
      .then((res) => {
        callback(res.data.data);
      });
  }

  applianceAvailable(callback) {
    axios.get("Appliance/Available", this._getAuthConfig()).then((res) => {
      callback(res.data.data);
    });
  }

  addAppliance(callback, serialnumber, nickname) {
    axios
      .post(
        "Appliance/AddAppliance",
        {
          serialnumber: serialnumber,
          nickname: nickname
        },
        this._getAuthConfig()
      )
      .then((res) => {
        callback(res.data.data);
      })
      .catch((error) => callback("error"));
  }

  changeNickname(callback, serialnumber, nickname) {
    axios
      .post(
        "Appliance/ChangeNickname",
        {
          serialnumber: serialnumber,
          nickname: nickname
        },
        this._getAuthConfig()
      )
      .then((res) => {
        callback(res.data.data);
      });
  }

  deleteAppliance(callback, serialnumber) {
    axios
      .delete("Appliance/DeleteAppliance", {
        params: {
          serialnumber: serialnumber
        },
        ...this._getAuthConfig()
      })
      .then((res) => {
        callback(res.data.data);
      });
  }

  getDwlConfigurationPage(callback, language, country, pageId, baseUrl) {
    if (baseUrl.indexOf("localhost") !== -1) {
      baseUrl = "https://www.liebherr.com";
    }

    axios
      .get("Configuration/Page", {
        params: {
          language: language,
          country: country.toUpperCase(),
          pageId: pageId,
          baseUrl: baseUrl
        },
        ...this._getAuthConfig()
      })
      .then((res) => {
        callback(res.data);
      });
  }
}
